import { useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/macro';
import { Box, Flex, Text } from '@mantine/core';
import { observer } from 'mobx-react-lite';

import { TicketsCount } from '../../../components/tickets-count/tickets-count';
import { UiCard } from '../../../components/ui/ui-card/ui-card';
import { UiNmberClickerInput } from '../../../components/ui/ui-number-clicker-input/ui-number-clicker-input';
import { nextIdFactory } from '../../../lib/next-id';
import { CalculatedTariff } from '../types';
import { MyPassenger, MyTariff } from './types';

const nextId = nextIdFactory();
export const PassengersAddControl = observer(
  ({
    tariffs = [],
    onChange,
  }: {
    tariffs: MyTariff[];
    onChange: (pass: MyPassenger[]) => void;
  }) => {
    const [passCount, setPassCount] = useState(1);
    const [passengers, setPassengers] = useState<MyPassenger[]>(() => [
      { id: nextId(), tariffs: [] },
    ]);

    const foundBaseTariff = useMemo(
      () => tariffs.find((t) => t.main === true),
      [tariffs]
    );

    const preparedTariffs = useMemo(
      () =>
        // display all tariffs except base tariff. Base tariff will be used with passenger by default
        tariffs.filter((tariff) => tariff.main !== true),
      [tariffs]
    );

    const handleOnChange = (pass: MyPassenger[]) => {
      // add base tariff to every passanger
      if (!foundBaseTariff) return;

      const result = pass.map((passenger) => {
        return {
          ...passenger,
          tariffs: passenger.tariffs.concat([
            {
              id: foundBaseTariff.id,
              price: foundBaseTariff.price,
              main: foundBaseTariff.main,
              count: 1,
              discountRules: foundBaseTariff.discountRules,
            },
          ]),
        };
      });

      onChange && onChange(result);
    };
    const handleChangePassengersCount = (value: number) => {
      let pass: MyPassenger[] = [];

      // do nothing
      if (passengers.length === value) return;

      // add new passenger
      if (passengers.length < value) {
        const id = nextId();
        pass = [...passengers, { id, tariffs: [] }];
      }

      // remove passenger
      if (passengers.length > value) {
        pass = passengers.slice(0, passengers.length - 1);
      }

      handleOnChange(pass);
      setPassengers(pass);
    };

    const handleChangePassengerTariffs = (passTariffs: {
      id: number;
      tariffs: CalculatedTariff[];
    }) => {
      const pass = passengers.map((passenger) => {
        if (passenger.id === passTariffs.id) {
          return {
            ...passenger,
            tariffs: passTariffs.tariffs,
          };
        }

        return passenger;
      });

      setPassengers(pass);
      handleOnChange(pass);
    };

    useEffect(() => {
      // emit default first passenger
      handleOnChange(passengers);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const passCountTitle = (
      <Text fw={700} span>
        <Trans>Пассажиры</Trans>
      </Text>
    );

    return (
      <>
        <UiCard title={passCountTitle}>
          <Flex justify="space-between" align="center">
            <span>
              <Trans>Количество пассажиров:</Trans>
            </span>
            <Box miw={150}>
              <UiNmberClickerInput
                value={passCount}
                min={1}
                max={4}
                step={1}
                onChange={(count) => {
                  setPassCount(count);
                  handleChangePassengersCount(count);
                }}
              />
            </Box>
          </Flex>
        </UiCard>
        {/* <UiCard title="Добавьте пассажиров">
          <Flex justify="space-between" align="center" mt="md">
            <span>Пассажиры:</span>
            <Box miw={150}>
              <UiNmberClickerInput
                min={1}
                max={10}
                disableEdit
                value={passengers.length}
                defaultValue={1}
                onChange={handleChangePassengersCount}
              />
            </Box>
          </Flex>
        </UiCard> */}

        <PassengerCard
          key={passengers[0].id}
          pass={passengers[0]}
          tariffs={preparedTariffs}
          onChangeTariffs={handleChangePassengerTariffs}
        />
      </>
    );
  }
);

type PassengerCardProps = {
  pass: { id: number };
  tariffs: MyTariff[];
  onChangeTariffs?: (pass: MyPassenger) => void;
};

const PassengerCard = observer(
  ({ pass, tariffs, onChangeTariffs }: PassengerCardProps) => {
    const handleChangeTariffs = (tariffs: CalculatedTariff[]) => {
      onChangeTariffs?.({ id: pass.id, tariffs });
    };

    const title = (
      <Text fw={700} span>
        <Trans>Тарифы</Trans>
      </Text>
    );
    return (
      <UiCard title={title}>
        <Box mt="md">
          <TicketsCount tariffs={tariffs} onCalculate={handleChangeTariffs} />
        </Box>
      </UiCard>
    );
  }
);
